import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { Variants } from '../types/common';

/**
 * Configuration passed to `showAlert`. `apiResponse` is `unknown` because
 * callers typically forward a caught error (`catch (error: unknown)`); the
 * slice normalises across the three error shapes the codebase produces.
 *
 * - axios errors:           `error.response.data.message`
 * - RTK Query `FetchBaseQueryError`: `error.data.message`
 * - `Error` / `SerializedError`:     `error.message`
 *
 * Pre Pass 8 the slice only handled the axios shape, so RTK-Query-backed
 * mutations silently fell back to the generic "An error occurred…" message.
 */
export interface AlertConfiguration {
  type:
    | Variants.DEFAULT
    | Variants.SUCCESS
    | Variants.ERROR
    | Variants.WARNING
    | Variants.INFO
    | Variants.CLEANUP;
  message?: string;
  apiResponse?: unknown;
  /** How long the toast stays, in ms. Omitted: the Toaster's default (6 s). */
  duration?: number;
}

/** A `duration` that keeps the toast until the user closes it. */
export const STAYS_UNTIL_DISMISSED = Infinity;

export interface APIErrorState {
  configuration: AlertConfiguration | null;
  dismiss: Record<string, never> | null;
}

const FALLBACK_MESSAGE =
  'An error occurred while making a API request. Network or server is not responding!';

/**
 * Pull a string message out of any of the three supported error shapes.
 * Returns null when none match — caller falls back to FALLBACK_MESSAGE.
 */
function extractErrorMessage(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;

  // axios: error.response.data.message
  const axiosMessage = (error as { response?: { data?: { message?: unknown } } })
    .response?.data?.message;
  if (typeof axiosMessage === 'string' && axiosMessage) return axiosMessage;

  // RTK Query FetchBaseQueryError: error.data.message
  const rtkMessage = (error as { data?: { message?: unknown } }).data?.message;
  if (typeof rtkMessage === 'string' && rtkMessage) return rtkMessage;

  // Error / SerializedError: error.message
  const flatMessage = (error as { message?: unknown }).message;
  if (typeof flatMessage === 'string' && flatMessage) return flatMessage;

  return null;
}

const initialState: APIErrorState = {
  configuration: null,
  dismiss: null,
};

export const AlertSlice = createSlice({
  name: 'alertState',
  initialState,
  reducers: {
    showAlert: (state, action: PayloadAction<AlertConfiguration>) => {
      const configuration: AlertConfiguration = { ...action.payload };
      if (!configuration.message) {
        configuration.message =
          extractErrorMessage(configuration.apiResponse) ?? FALLBACK_MESSAGE;
      }
      state.configuration = configuration;
    },
    dismissAlert: (state) => {
      state.configuration = null;
      state.dismiss = {};
    },
  },
});

// Action creators are generated for each case reducer function
export const { showAlert, dismissAlert } = AlertSlice.actions;

export default AlertSlice.reducer;
